/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type {
  StoredUserPreferences,
  UserPreferencesPatch,
  UserPreferencesResponse,
} from '@poweralpha/common';
import { api } from '../../api';

/**
 * Last known overrides, so the first paint (theme, dock position) is right before the desktop
 * session loads, and so settings still stick locally when the session API is unavailable.
 */
const CACHE_KEY = 'pa:user-preferences';
/** Pre-settings key written by the old user menu; moved into the preferences once. */
const LEGACY_THEME_KEY = 'theme-mode';
const LEGACY_THEMES: Record<string, 'system' | 'dark' | 'light'> = {
  automatic: 'system',
  dark: 'dark',
  light: 'light',
};

export const readCachedPreferences = (): StoredUserPreferences => {
  try {
    const value = JSON.parse(localStorage.getItem(CACHE_KEY) ?? '{}');
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  } catch {
    return {};
  }
};

export const writeCachedPreferences = (preferences: StoredUserPreferences) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(preferences));
  } catch {
    // Storage full or blocked: the in-memory value still applies for this page.
  }
};

/** The patch that carries settings from legacy local keys the user hasn't overridden yet. */
export const readLegacyPreferencesPatch = (
  stored: StoredUserPreferences,
): UserPreferencesPatch | null => {
  if (stored.appearance?.theme !== undefined) {
    return null;
  }
  try {
    const legacyTheme = JSON.parse(localStorage.getItem(LEGACY_THEME_KEY) ?? 'null');
    const theme = LEGACY_THEMES[legacyTheme as string];
    return theme ? { set: { 'appearance.theme': theme } } : null;
  } catch {
    return null;
  }
};

export const removeLegacyPreferences = () => localStorage.removeItem(LEGACY_THEME_KEY);

export const saveUserPreferences = (patch: UserPreferencesPatch) =>
  api.post('desktop/preferences', { json: patch }).json<UserPreferencesResponse>();
